import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, RotateCcwIcon, CheckCircleIcon } from 'lucide-react';
interface RestoreEducatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  educatorName: string;
  archivedOn?: string;
  archivedReason?: string;
}
export const RestoreEducatorModal: React.FC<RestoreEducatorModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  educatorName,
  archivedOn,
  archivedReason
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Error restoring educator:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };
  return <AnimatePresence data-id="element-2162">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-50" onClick={handleClose} data-id="element-2163" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose} data-id="element-2164">
            <div className="bg-surface-elevated rounded-2xl shadow-2xl max-w-md w-full border border-custom" onClick={e => e.stopPropagation()} data-id="element-2165">
              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b border-custom" data-id="element-2166">
                <div className="flex items-center gap-3" data-id="element-2167">
                  <div className="w-10 h-10 rounded-full bg-[#00B75F] bg-opacity-10 flex items-center justify-center" data-id="element-2168">
                    <RotateCcwIcon size={20} className="text-[#00B75F]" data-id="element-2169" />
                  </div>
                  <div data-id="element-2170">
                    <h2 className="text-xl font-bold text-text-primary" data-id="element-2171">
                      Restore Educator
                    </h2>
                    <p className="text-sm text-text-secondary mt-0.5" data-id="element-2172">
                      {educatorName}
                    </p>
                  </div>
                </div>
                <button onClick={handleClose} disabled={isSubmitting} className="p-2 rounded-lg hover:bg-surface transition-colors disabled:opacity-50" aria-label="Close modal" data-id="element-2173">
                  <XIcon size={20} className="text-text-secondary" data-id="element-2174" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4" data-id="element-2175">
                {/* Archive Info */}
                {(archivedOn || archivedReason) && <div className="bg-surface rounded-xl p-4 border border-custom space-y-2" data-id="element-2176">
                    <h3 className="text-xs font-bold text-text-secondary uppercase tracking-wide" data-id="element-2177">
                      Archive Information
                    </h3>
                    {archivedOn && <div className="flex justify-between text-sm" data-id="element-2178">
                        <span className="text-text-tertiary" data-id="element-2179">Archived On:</span>
                        <span className="text-text-primary font-medium" data-id="element-2180">
                          {archivedOn}
                        </span>
                      </div>}
                    {archivedReason && <div className="text-sm" data-id="element-2181">
                        <span className="text-text-tertiary block mb-1" data-id="element-2182">
                          Reason:
                        </span>
                        <p className="text-text-primary" data-id="element-2183">{archivedReason}</p>
                      </div>}
                  </div>}

                {/* Info Box */}
                <div className="bg-[#00B75F] bg-opacity-5 border border-[#00B75F] border-opacity-20 rounded-xl p-4" data-id="element-2184">
                  <div className="flex items-start gap-3" data-id="element-2185">
                    <CheckCircleIcon size={20} className="text-[#00B75F] flex-shrink-0 mt-0.5" data-id="element-2186" />
                    <div className="space-y-2 text-sm" data-id="element-2187">
                      <p className="text-text-primary font-medium" data-id="element-2188">
                        Restoring will:
                      </p>
                      <ul className="space-y-1.5 text-text-secondary" data-id="element-2189">
                        <li className="flex items-start gap-2" data-id="element-2190">
                          <span className="text-[#00B75F] font-bold" data-id="element-2191">✓</span>
                          <span data-id="element-2192">
                            Restore full access to organization resources
                          </span>
                        </li>
                        <li className="flex items-start gap-2" data-id="element-2193">
                          <span className="text-[#00B75F] font-bold" data-id="element-2194">✓</span>
                          <span data-id="element-2195">
                            Restore all educator permissions and capabilities
                          </span>
                        </li>
                        <li className="flex items-start gap-2" data-id="element-2196">
                          <span className="text-[#00B75F] font-bold" data-id="element-2197">✓</span>
                          <span data-id="element-2198">
                            Make educator visible as "Active" in Overview
                          </span>
                        </li>
                        <li className="flex items-start gap-2" data-id="element-2199">
                          <span className="text-[#00B75F] font-bold" data-id="element-2200">✓</span>
                          <span data-id="element-2201">
                            All historical data and activity remains intact
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-custom" data-id="element-2202">
                <button onClick={handleClose} disabled={isSubmitting} className="px-5 py-2.5 border-2 border-custom text-text-primary rounded-xl font-semibold hover:bg-surface transition-colors disabled:opacity-50" data-id="element-2203">
                  Cancel
                </button>
                <button onClick={handleConfirm} disabled={isSubmitting} className="px-5 py-2.5 bg-[#00B75F] text-white rounded-xl font-semibold hover:bg-[#00a055] transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2" data-id="element-2204">
                  {isSubmitting ? <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2205" />
                      Restoring...
                    </> : <>
                      <RotateCcwIcon size={16} data-id="element-2206" />
                      Restore Educator
                    </>}
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};